import React, { FC, useState } from 'react';
import { Wrapper } from 'components/theme/Wrapper';
import {
  ResourceHeading2,
  ResourceParagraph,
} from 'components/theme/Typography/ResourceTypography';
import { NavigationHeader } from 'components/NavigationHeader';
import { useTranslation } from 'react-i18next';
import {
  LayoutWrapper,
  Header,
  Main,
} from 'features/GuestReschedule/components/Layout';
import { PrimaryButton } from 'components/theme/Button';
import { useHistory } from 'react-router-dom';
import ReservationItem from 'features/GuestReschedule/components/ReservationItem';
import useGuestReschedule from 'features/GuestReschedule/hooks/useGuestReschedule';
import useInitGuestReschedule from 'features/GuestReschedule/hooks/useInitGuestReschedule';
import NoBookingsAvailable from 'features/GuestReschedule/components/NoBookingsAvailable';
import { rescheduleBooking } from 'api/reschedule/rescheduleBooking';
import { useToastContext } from 'ToastProvider';
import { useBrandId } from 'AppProvider';

const GuestRescheduleConfirmationPage: FC = () => {
  const { t } = useTranslation();
  const brandId = useBrandId();
  const { push, replace } = useHistory();
  const { showToast: toast } = useToastContext();

  useInitGuestReschedule();

  const {
    bookingSelected,
    selectedBookingId,
    cartId,
    selectedRescheduleDate,
    selectedResourceId,
    token,
  } = useGuestReschedule();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async () => {
    if (!selectedRescheduleDate || !bookingSelected || !selectedBookingId)
      return;

    setIsSubmitting(true);

    try {
      await rescheduleBooking(selectedBookingId, brandId, {
        sessionTime: selectedRescheduleDate,
        sessionDuration: bookingSelected.eventSessionDurationInMinutes,
        sendConfirmationEmail: true,
        ...(selectedResourceId && { experienceId: selectedResourceId }),
      });

      replace({
        pathname: `/carts/${cartId}/guest-reschedule/booking/${selectedBookingId}/success`,
        search: `?token=${token}`,
      });
    } catch (error) {
      toast({
        type: 'failure',
        title: t('somethingWentWrong'),
      });
      console.error('Reschedule failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!bookingSelected || !selectedRescheduleDate) {
    return <NoBookingsAvailable title={t('confirmReservation')} />;
  }

  const notes = bookingSelected.reschedulePolicyNotes;

  return (
    <Wrapper>
      <LayoutWrapper>
        <NavigationHeader
          preventDefaultBack
          onGoBack={() =>
            push({
              pathname: `/carts/${cartId}/guest-reschedule/booking/${selectedBookingId}/date`,
              search: `?token=${token}`,
            })
          }
        />
        <Header>
          <ResourceHeading2 fontSize="28px" className="title">
            {t('confirmReservation')}
          </ResourceHeading2>
          {notes && (
            <ResourceParagraph
              className="subtitle"
              fontSize="16px"
              opacity="0.7"
              fontWeight="400"
              lineHeight="20px"
            >
              {notes}
            </ResourceParagraph>
          )}
        </Header>
        <Main>
          <ReservationItem
            booking={bookingSelected}
            rescheduledDate={selectedRescheduleDate}
            isSelectable={false}
          />
        </Main>
        <PrimaryButton big disabled={isSubmitting} onClick={handleContinue}>
          {t('confirm')}
        </PrimaryButton>
      </LayoutWrapper>
    </Wrapper>
  );
};

export default GuestRescheduleConfirmationPage;
